import { range } from './functional/array.no-deps'
import { getDuration, oneDayInMs, oneHourInMs, oneMinuteInMs, oneSecondInMs } from './duration.no-deps'
import { maxInt } from './number.no-deps'

export type RelativeTimeWindow =
    | Timestamp
    | DaysAgo
    | HoursAgo
    | MinutesAgo
    | LessThanAMinuteAgo
    | AFewSecondsAgo
    | JustNow
    | InSeconds
    | InLessThanAMinute
    | InMinutes
    | InHours
    | InDays

export type Timestamp = _TimeWindow<'Timestamp'>
export type DaysAgo = _TimeWindow<'DaysAgo', { days: number }>
export type HoursAgo = _TimeWindow<'HoursAgo', { hours: number }>
export type MinutesAgo = _TimeWindow<'MinutesAgo', { minutes: number }>
export type LessThanAMinuteAgo = _TimeWindow<'LessThanAMinuteAgo'>
export type AFewSecondsAgo = _TimeWindow<'AFewSecondsAgo'>
export type JustNow = _TimeWindow<'JustNow'>
export type InSeconds = _TimeWindow<'InSeconds', { seconds: number }>
export type InLessThanAMinute = _TimeWindow<'InLessThanAMinute'>
export type InMinutes = _TimeWindow<'InMinutes', { minutes: number }>
export type InHours = _TimeWindow<'InHours', { hours: number }>
export type InDays = _TimeWindow<'InDays', { days: number }>

type _TimeWindow<T, E = {}> = E & {
    type: T
    min: number
    max: number
}

function reverseRangeMap<T>(lower: number, upper: number, fn: (n: number) => T) {
    return range(lower, upper).reverse().map(fn)
}

export const relativeTimeWindows: RelativeTimeWindow[] = [
    {
        type: 'Timestamp' as const,
        min: 14 * oneDayInMs,
        max: maxInt,
    },

    // in 2-13 days
    ...reverseRangeMap(2, 13, (days) => {
        return {
            type: 'InDays' as const,
            days,
            min: days * oneDayInMs,
            max: (days + 1) * oneDayInMs - 1,
        }
    }),

    // in 1-47 hours
    ...reverseRangeMap(1, 47, (hours) => {
        return {
            type: 'InHours' as const,
            hours,
            min: hours * oneHourInMs,
            max: (hours + 1) * oneHourInMs - 1,
        }
    }),

    // in 1-59 minutes
    ...reverseRangeMap(1, 59, (minutes) => {
        return {
            type: 'InMinutes' as const,
            minutes,
            min: minutes * oneMinuteInMs,
            max: (minutes + 1) * oneMinuteInMs - 1,
        }
    }),

    // in 10-50 seconds
    {
        type: 'InLessThanAMinute' as const,
        min: 10 * oneSecondInMs + 1,
        max: oneMinuteInMs - 1,
    },

    // in 1-10 seconds
    ...reverseRangeMap(1, 10, (seconds) => {
        return {
            type: 'InSeconds' as const,
            seconds,
            min: (seconds - 1) * oneSecondInMs + 1,
            max: seconds * oneSecondInMs,
        }
    }),

    // JustNow
    {
        type: 'JustNow' as const,
        min: -3 * oneSecondInMs + 1,
        max: 0,
    },

    // AFewSecondsAgo
    {
        type: 'AFewSecondsAgo' as const,
        min: -10 * oneSecondInMs + 1,
        max: -3 * oneSecondInMs,
    },

    // LessThanAMinuteAgo
    {
        type: 'LessThanAMinuteAgo' as const,
        min: -1 * oneMinuteInMs + 1,
        max: -10 * oneSecondInMs,
    },

    // 1-59 MinutesAgo
    ...reverseRangeMap(-59, -1, (minutes) => {
        return {
            type: 'MinutesAgo' as const,
            minutes: -minutes,
            min: (minutes - 1) * oneMinuteInMs + 1,
            max: minutes * oneMinuteInMs,
        }
    }),

    // 1-47 HoursAgo
    ...reverseRangeMap(-47, -1, (hours) => {
        return {
            type: 'HoursAgo' as const,
            hours: -hours,
            min: (hours - 1) * oneHourInMs + 1,
            max: hours * oneHourInMs,
        }
    }),

    // 2-13 DaysAgo
    ...reverseRangeMap(-13, -2, (days) => {
        return {
            type: 'DaysAgo' as const,
            days: -days,
            min: (days - 1) * oneDayInMs + 1,
            max: days * oneDayInMs,
        }
    }),

    {
        type: 'Timestamp' as const,
        min: -maxInt,
        max: -14 * oneDayInMs,
    },
]

export function getRelativeTimeWindow(date: Date, now: Date = new Date()) {
    const index = getIndex(date, now)
    return relativeTimeWindows[index] ?? null
}

function getNextWindow(date: Date, now: Date = new Date()) {
    const nextIndex = getIndex(date, now) + 1
    return relativeTimeWindows[nextIndex] ?? null
}

function getIndex(date: Date, now: Date = new Date()) {
    const duration = getDuration(date, now)
    return relativeTimeWindows.findIndex((w) => w.min <= duration && w.max >= duration)
}

export function getTimeToNextWindow(date: Date, now: Date = new Date()) {
    const next = getNextWindow(date, now)
    if (next == null) return null

    const duration = getDuration(date, now)

    /**
     * It's not intuitive but it turns out that you always want the maximum of the next window even when in the past
     * This is because as now is always going up of time, which means duration is always going down.
     *
     * For instance, if the date is 20 minutes in the future, then the duration from now will be +20m.
     * Time will be moving forward, so the next duration window will be "in 19 minutes".
     * So the max for that window will be 19m 59s 999ms.
     * So the calculation is
     *     duration - next.max = (20m - 19m 59s 999ms) = 1ms
     *
     * If the date was 20 minutes in the past, then duration from now will be -20m.
     * Time will still be moving forward, which means the next duration will be "21 minutes ago".
     * So the max for that window -21 minutes 0 seconds 0 milliseconds
     * (Note the max is not -21m 59s 999ms which might seem more intuitive)
     * So the calculation is
     *     duration - next.max = (-20m - (-21m 0s 0ms)) = +1m
     */
    return duration - next.max
}
